import React, { useEffect, useMemo, useState } from 'react';
import { visitLogsAPI } from '../services/api';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { FileText, Clock, Note, Printer, User, CheckCircle, Circle, ClipboardText } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { format, parseISO, startOfWeek, endOfWeek, subWeeks } from 'date-fns';

const fmt = (d) => format(d, 'yyyy-MM-dd');
const PRESETS = [
  { key: 'today', label: 'Today' },
  { key: 'this_week', label: 'This Week' },
  { key: 'last_week', label: 'Last Week' },
  { key: 'custom', label: 'Custom' },
];

export const rangeForPreset = (preset, custom) => {
  const now = new Date();
  if (preset === 'today') return { from: fmt(now), to: fmt(now) };
  if (preset === 'this_week') return { from: fmt(startOfWeek(now)), to: fmt(endOfWeek(now)) };
  if (preset === 'last_week') { const lw = subWeeks(now, 1); return { from: fmt(startOfWeek(lw)), to: fmt(endOfWeek(lw)) }; }
  return { from: custom.from || '', to: custom.to || '' };
};

const LOG_TYPE_COLORS = {
  general: 'bg-blue-100 text-blue-800', observation: 'bg-green-100 text-green-800',
  incident: 'bg-red-100 text-red-800', activity: 'bg-yellow-100 text-yellow-800',
  clock_out: 'bg-teal-100 text-teal-800',
};

const fmtTime = (iso) => { try { return iso ? format(new Date(iso), 'h:mm a') : '—'; } catch { return '—'; } };

const ClockOutBody = ({ log }) => {
  const tasks = log.tasks_completed || [];
  const done = tasks.filter(t => t.completed).length;
  return (
    <div className="space-y-3" data-testid={`clockout-body-${log.visit_id}`}>
      <p className="text-xs text-muted-foreground flex items-center gap-1"><Clock weight="duotone" className="w-3.5 h-3.5" /> Clocked in {fmtTime(log.check_in_time)} · out {fmtTime(log.check_out_time)}</p>
      {tasks.length > 0 && (
        <div className="rounded-xl border border-border bg-muted/30 p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Care plan checklist · {done}/{tasks.length} done</p>
          <ul className="space-y-1.5">
            {tasks.map((t, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                {t.completed ? <CheckCircle weight="fill" className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> : <Circle className="w-4 h-4 text-muted-foreground/60 mt-0.5 shrink-0" />}
                <div className="min-w-0">
                  <span className={t.completed ? '' : 'text-muted-foreground line-through'}>{t.name}</span>
                  {t.required && <Badge variant="outline" className="rounded-full text-[9px] ml-1.5 py-0">Required</Badge>}
                  {t.notes && <p className="text-xs text-muted-foreground italic">“{t.notes}”</p>}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      {log.content && <div><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Visit comments</p><p className="text-sm text-foreground/90 whitespace-pre-wrap">{log.content}</p></div>}
    </div>
  );
};

const printNotes = (groups, clientLabel, rangeLabel) => {
  const esc = (s) => String(s ?? '').replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
  const noteHtml = (l) => {
    if (l.entry_type === 'clock_out') {
      const tasks = (l.tasks_completed || []).map(t => `<li>${t.completed ? '☑' : '☐'} ${esc(t.name)}${t.notes ? ` <em>— ${esc(t.notes)}</em>` : ''}</li>`).join('');
      return `<div class="note"><div class="meta"><strong>${esc(l.client_name)}</strong> · ${esc(l.visit_time || '')} · ${esc(l.caregiver_name)} · <span class="type">Clock-Out Report</span></div>
        ${tasks ? `<ul class="tasks">${tasks}</ul>` : ''}${l.content ? `<p><strong>Visit comments:</strong> ${esc(l.content).replace(/\n/g, '<br>')}</p>` : ''}</div>`;
    }
    return `<div class="note"><div class="meta"><strong>${esc(l.client_name)}</strong> · ${esc(l.visit_time || '')} · ${esc(l.caregiver_name)} · <span class="type">${esc(l.log_type)}</span></div><p>${esc(l.content).replace(/\n/g, '<br>')}</p></div>`;
  };
  const body = groups.map(([day, logs]) => `
    <h2>${esc(format(parseISO(day), 'EEEE, MMMM d, yyyy'))}</h2>
    ${logs.map(noteHtml).join('')}`).join('');
  const w = window.open('', '_blank');
  if (!w) { toast.error('Pop-up blocked — allow pop-ups to print'); return; }
  w.document.write(`<!doctype html><html><head><title>Care Notes — ${esc(clientLabel)}</title>
    <style>body{font-family:Segoe UI,Arial,sans-serif;color:#1e293b;max-width:800px;margin:32px auto;padding:0 20px}h1{font-size:22px;margin:0}h2{font-size:15px;margin:28px 0 8px;padding-bottom:4px;border-bottom:1px solid #cbd5e1}.sub{color:#64748b;font-size:13px;margin-top:4px}.note{border:1px solid #e2e8f0;border-radius:10px;padding:12px 14px;margin:8px 0;page-break-inside:avoid}.meta{font-size:12px;color:#475569;margin-bottom:6px}.type{text-transform:capitalize;background:#f1f5f9;padding:1px 8px;border-radius:999px}p{margin:0;font-size:14px;white-space:pre-wrap}.tasks{margin:4px 0 8px;padding-left:4px;list-style:none;font-size:13px}.tasks li{margin:2px 0}@media print{body{margin:0}}</style></head>
    <body><h1>Care Notes — ${esc(clientLabel)}</h1><div class="sub">${esc(rangeLabel)} · Printed ${esc(format(new Date(), 'MMM d, yyyy h:mm a'))}</div>${body || '<p class="sub">No notes in this range.</p>'}</body></html>`);
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 300);
};

/**
 * Care-notes feed filtered by client + day/week range, grouped by day.
 * If `clientId` is passed the client picker is hidden and the feed is locked to that client.
 */
const CareNotesFeed = ({ clientId, clients = [], refreshKey = 0, defaultPreset = 'this_week' }) => {
  const [selectedClient, setSelectedClient] = useState(clientId || 'all');
  const [preset, setPreset] = useState(defaultPreset);
  const [custom, setCustom] = useState({ from: fmt(startOfWeek(new Date())), to: fmt(new Date()) });
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const range = useMemo(() => rangeForPreset(preset, custom), [preset, custom]);
  const effectiveClient = clientId || (selectedClient !== 'all' ? selectedClient : null);

  useEffect(() => {
    if (preset === 'custom' && (!range.from || !range.to)) return;
    setLoading(true);
    visitLogsAPI.getAll({ client_id: effectiveClient || undefined, date_from: range.from, date_to: range.to })
      .then(res => setLogs(res.data))
      .catch(() => toast.error('Failed to load care notes'))
      .finally(() => setLoading(false));
  }, [effectiveClient, range.from, range.to, preset, refreshKey]);

  const groups = useMemo(() => {
    const m = new Map();
    logs.forEach(l => { const k = l.visit_date || 'unknown'; if (!m.has(k)) m.set(k, []); m.get(k).push(l); });
    return [...m.entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [logs]);

  const clientLabel = effectiveClient ? (clients.find(c => c.id === effectiveClient)?.name || logs[0]?.client_name || 'Client') : 'All Clients';
  const rangeLabel = range.from && range.to ? (range.from === range.to ? format(parseISO(range.from), 'MMM d, yyyy') : `${format(parseISO(range.from), 'MMM d')} – ${format(parseISO(range.to), 'MMM d, yyyy')}`) : 'All dates';

  return (
    <div className="space-y-4" data-testid="care-notes-feed">
      <div className="flex flex-col lg:flex-row lg:items-center gap-3">
        {!clientId && (
          <Select value={selectedClient} onValueChange={setSelectedClient}>
            <SelectTrigger className="rounded-xl lg:w-64" data-testid="notes-client-select"><User className="w-4 h-4 mr-2 text-muted-foreground" /><SelectValue placeholder="All clients" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All clients</SelectItem>
              {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <div className="flex flex-wrap items-center gap-1 rounded-xl bg-muted p-1" data-testid="notes-range-presets">
          {PRESETS.map(p => (
            <Button key={p.key} size="sm" variant={preset === p.key ? 'default' : 'ghost'} className="rounded-lg h-8" onClick={() => setPreset(p.key)} data-testid={`notes-preset-${p.key}`}>{p.label}</Button>
          ))}
        </div>
        {preset === 'custom' && (
          <div className="flex items-center gap-2">
            <Input type="date" value={custom.from} onChange={(e) => setCustom({ ...custom, from: e.target.value })} className="rounded-xl h-9 w-40" data-testid="notes-date-from" />
            <span className="text-muted-foreground text-sm">to</span>
            <Input type="date" value={custom.to} onChange={(e) => setCustom({ ...custom, to: e.target.value })} className="rounded-xl h-9 w-40" data-testid="notes-date-to" />
          </div>
        )}
        <div className="lg:ml-auto flex items-center gap-3">
          <span className="text-sm text-muted-foreground tabular-nums" data-testid="notes-count">{logs.length} note{logs.length !== 1 ? 's' : ''} · {rangeLabel}</span>
          <Button variant="outline" size="sm" className="rounded-xl gap-1" onClick={() => printNotes(groups, clientLabel, rangeLabel)} disabled={!logs.length} data-testid="notes-print-btn"><Printer className="w-4 h-4" /> Print</Button>
        </div>
      </div>

      {loading ? <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>
      : groups.length === 0 ? (
        <Card className="rounded-2xl border border-border"><CardContent className="flex flex-col items-center justify-center py-12">
          <FileText weight="duotone" className="w-16 h-16 text-muted-foreground/50 mb-4" />
          <h3 className="font-heading text-lg font-medium">No care notes {rangeLabel !== 'All dates' ? `for ${rangeLabel}` : ''}</h3>
          <p className="text-muted-foreground text-sm mt-1">Try another client or date range</p>
        </CardContent></Card>
      ) : groups.map(([day, dayLogs]) => (
        <div key={day} className="space-y-3" data-testid={`notes-day-${day}`}>
          <div className="flex items-center gap-3 pt-2">
            <h3 className="font-heading font-semibold text-sm uppercase tracking-wide text-muted-foreground">{day !== 'unknown' ? format(parseISO(day), 'EEEE, MMM d, yyyy') : 'Unknown date'}</h3>
            <div className="flex-1 h-px bg-border" />
            <Badge variant="secondary" className="rounded-full text-[10px]">{dayLogs.length}</Badge>
          </div>
          {dayLogs.map(log => (
            <Card key={log.id} className="rounded-2xl border border-border hover:border-border/80 hover:shadow-sm transition-all duration-200" data-testid={`log-card-${log.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${log.entry_type === 'clock_out' ? 'bg-teal-100' : 'bg-primary/10'}`}>{log.entry_type === 'clock_out' ? <ClipboardText weight="duotone" className="w-5 h-5 text-teal-700" /> : <Note weight="duotone" className="w-5 h-5 text-primary" />}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <div className="min-w-0">
                        {!clientId && <p className="font-medium text-foreground truncate">{log.client_name}</p>}
                        <p className="text-sm text-muted-foreground flex items-center gap-1 flex-wrap"><Clock weight="duotone" className="w-4 h-4" />{log.visit_time || '—'} · by {log.caregiver_name}</p>
                      </div>
                      <Badge className={`${LOG_TYPE_COLORS[log.log_type] || 'bg-gray-100 text-gray-800'} rounded-full shrink-0 capitalize`}>{log.entry_type === 'clock_out' ? 'Clock-Out Report' : log.log_type}</Badge>
                    </div>
                    {log.entry_type === 'clock_out' ? <ClockOutBody log={log} /> : <p className="text-sm text-foreground/90 whitespace-pre-wrap">{log.content}</p>}
                    <p className="text-xs text-muted-foreground mt-3">{log.entry_type === 'clock_out' ? 'Submitted at clock-out' : 'Logged'} {format(new Date(log.created_at), 'MMM d, yyyy h:mm a')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ))}
    </div>
  );
};

export default CareNotesFeed;
